import { useEffect, useState } from "react";
import { AlertCircle, ClipboardList, Edit2, Loader2, Plus, Save, X } from "lucide-react";
import { toast } from "react-toastify";

const EMPTY_DRAFT = {
  type: "",
  description: "",
  nextBookingAction: "",
  important: false,
};

const isMongoId = (value) => typeof value === "string" && /^[a-f\d]{24}$/i.test(value);

const CustomerHistoryModal = ({ axiosAdmin, customer, onClose, onUpdated }) => {
  const [records, setRecords] = useState([]);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const customerIdentifier = isMongoId(customer._id) ? customer._id : customer.phone;

  useEffect(() => {
    let active = true;
    setLoading(true);

    axiosAdmin
      .get(`/api/admin/customer-history/${encodeURIComponent(customerIdentifier)}`)
      .then((response) => {
        if (active) {
          setRecords(response.data.records || []);
          onUpdated(customer._id, customer.phone, Boolean(response.data.hasImportantHistory));
        }
      })
      .catch((error) => {
        if (active) {
          toast.error(error?.response?.data?.message || "خطا در دریافت سابقه مشتری");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [axiosAdmin, customer._id, customer.phone, customerIdentifier, onUpdated]);

  const startEditing = (record) => {
    setEditingId(record._id);
    setDraft({
      type: record.type || "",
      description: record.description || "",
      nextBookingAction: record.nextBookingAction || "",
      important: Boolean(record.important),
    });
  };

  const resetDraft = () => {
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
  };

  const saveRecord = async (event) => {
    event.preventDefault();
    if (!draft.type.trim() || !draft.description.trim()) {
      toast.warning("نوع سابقه و توضیحات را وارد کنید");
      return;
    }

    setSaving(true);
    try {
      const url = `/api/admin/customer-history/${encodeURIComponent(customerIdentifier)}`;
      const response = editingId
        ? await axiosAdmin.put(`${url}/${editingId}`, draft)
        : await axiosAdmin.post(url, draft);
      setRecords(response.data.records || []);
      onUpdated(customer._id, customer.phone, Boolean(response.data.hasImportantHistory));
      resetDraft();
      toast.success(editingId ? "سابقه مشتری بروزرسانی شد" : "سابقه مشتری ثبت شد");
    } catch (error) {
      toast.error(error?.response?.data?.message || "خطا در ذخیره سابقه مشتری");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <section
        dir="rtl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="customer-history-title"
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-5 py-4">
          <div className="flex items-center gap-3">
            <ClipboardList className="h-5 w-5 text-purple-600" />
            <div>
              <h2 id="customer-history-title" className="font-bold text-gray-900">
                سابقه مشتری
              </h2>
              <p className="text-sm text-gray-500">{customer.name} · {customer.phone}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="بستن" className="rounded-lg p-2 text-gray-500 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="space-y-5 p-5">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>در حال دریافت سوابق...</span>
            </div>
          ) : records.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 py-8 text-center text-sm text-gray-500">
              سابقه‌ای برای این مشتری ثبت نشده است.
            </div>
          ) : (
            <div className="space-y-3">
              {records.map((record) => (
                <article key={record._id} className="rounded-xl border border-gray-200 p-4">
                  <div className="mb-2 flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{record.type}</h3>
                      {record.important && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                          <span className="h-2 w-2 rounded-full bg-red-600" />
                          مهم
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => startEditing(record)}
                      className="inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-sm text-purple-700 hover:bg-purple-50"
                    >
                      <Edit2 className="h-4 w-4" />
                      ویرایش
                    </button>
                  </div>
                  <p className="whitespace-pre-wrap text-sm leading-6 text-gray-700">{record.description}</p>
                  {record.nextBookingAction && (
                    <div className="mt-3 flex gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                      <div>
                        <span className="font-semibold">اقدام لازم برای رزرو بعدی: </span>
                        <span className="whitespace-pre-wrap">{record.nextBookingAction}</span>
                      </div>
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}

          <form onSubmit={saveRecord} className="space-y-3 rounded-xl border border-purple-100 bg-purple-50/50 p-4">
            <h3 className="font-semibold text-gray-900">
              {editingId ? "ویرایش سابقه" : "ثبت سابقه جدید"}
            </h3>
            <label className="block text-sm font-medium text-gray-700">
              نوع سابقه
              <input
                value={draft.type}
                onChange={(event) => setDraft((current) => ({ ...current, type: event.target.value }))}
                maxLength={100}
                required
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 outline-none focus:border-purple-500"
                placeholder="مثلاً حساسیت یا نکته مهم"
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              توضیحات
              <textarea
                value={draft.description}
                onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
                maxLength={2000}
                required
                rows={3}
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 outline-none focus:border-purple-500"
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              اقدام لازم برای رزروهای بعدی
              <textarea
                value={draft.nextBookingAction}
                onChange={(event) => setDraft((current) => ({ ...current, nextBookingAction: event.target.value }))}
                maxLength={1000}
                rows={2}
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 outline-none focus:border-purple-500"
              />
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={draft.important}
                onChange={(event) => setDraft((current) => ({ ...current, important: event.target.checked }))}
                className="h-4 w-4 accent-red-600"
              />
              سابقه مهم است و باید کنار نام مشتری مشخص شود
            </label>
            <div className="flex justify-end gap-2 pt-1">
              {editingId && (
                <button type="button" onClick={resetDraft} className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-white">
                  انصراف از ویرایش
                </button>
              )}
              <button
                type="submit"
                disabled={saving || loading}
                className="inline-flex items-center gap-2 rounded-lg bg-purple-700 px-4 py-2 text-sm font-medium text-white hover:bg-purple-800 disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {saving ? "در حال ذخیره..." : editingId ? "ذخیره تغییرات" : "ثبت سابقه"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default CustomerHistoryModal;
